import { useQuery } from '@apollo/client';
import { GET_CURRENT_USER_DATA } from '@pass-frontend/common/data-access';
import {
  AuthStatus,
  CurrentState,
  DependentInfo,
  KeyNamePair,
  Member_Pass as Member,
  ServiceSectorKey,
  Sponsor,
  Tier_Pass as Tier,
  TierGroup,
} from '@pass-frontend/data-access/common/edge';

import { env } from '@pass-frontend/features/configuration/util';
import { clientInformation, SponsorType } from './is-suppressed-sponsor';
import { doesTierGroupContainSubsidized } from '../checks/does-tier-group-contain-subsidized';

const { FF_ENABLE_COMMERCIAL_SUBSIDIZED_FAMILY_MEMBERSHIP } = env();

export const useCurrentUserData = () => {
  const { loading, error, data } = useQuery<{
    currentMember: Member;
    currentState: CurrentState;
    currentSponsor: Pick<
      Sponsor,
      | 'sponsorId'
      | 'product'
      | 'paymentRequired'
      | 'serviceSector'
      | 'hipaaConsentRequired'
      | 'clientNameAffiliations'
      | 'clientLogoUrl'
      | 'isDependentMinAge16'
      | 'rewardsPaymentSystem'
    >;
    getDependentInfo: DependentInfo;
    currentTierGroup: Pick<TierGroup, 'tiers' | 'serviceSector'>;
  }>(GET_CURRENT_USER_DATA, {
    ssr: false, // SKIP ON SSR COMPLETELY
    fetchPolicy: 'cache-first',
  });

  const isLocationPopulated = (
    lastLocation?: {
      latitude: number;
      longitude: number;
    } | null,
  ) => {
    if (
      lastLocation &&
      !isNaN(lastLocation?.latitude) &&
      lastLocation?.latitude !== 0 &&
      !isNaN(lastLocation?.longitude) &&
      lastLocation?.longitude !== 0
    ) {
      return true;
    } else {
      return false;
    }
  };

  const {
    currentMember,
    currentState,
    currentSponsor,
    getDependentInfo,
    currentTierGroup,
  } = data || {};

  const containsFree = doesTierGroupContainSubsidized(currentTierGroup);

  const authTags =
    currentTierGroup?.tiers &&
    (currentTierGroup.tiers[0]?.authTags as Tier['authTags']);
  const authKeys: string[] =
    (authTags &&
      authTags
        .map((feature: KeyNamePair | null) => feature?.key ?? '')
        .filter((key) => key.length > 0)) ||
    [];

  const isQualifiedUser = currentState?.authStatus === AuthStatus.qualified;
  const isUnverifiedUser = currentState?.authStatus === AuthStatus.unverified;
  const isImpersonatedView = !!currentState?.isImpersonatedView;

  const isGovernment =
    currentSponsor?.serviceSector?.key === ServiceSectorKey.government;

  const isUserMA =
    clientInformation(currentSponsor?.sponsorId) === SponsorType.MA;

  const isUserMedSupp =
    clientInformation(currentSponsor?.sponsorId) === SponsorType.MedSupp;

  return {
    loading: loading || false,
    error: error || false,
    currentMember,
    currentState,
    currentTierGroup: currentTierGroup || null,
    currentSponsor: currentSponsor || null,
    isRenewActive: currentState?.isRenewActive || false,
    lastLocation: currentMember?.lastLocation || {
      latitude: 0,
      longitude: 0,
    },
    isLocationPopulated:
      !!currentMember && isLocationPopulated(currentMember?.lastLocation),
    canAddFamilyMember: getDependentInfo?.canAddFamilyMember,
    isSubsidizedUser: containsFree,
    shouldDisplayFamilyMembershipPromo:
      (!containsFree || FF_ENABLE_COMMERCIAL_SUBSIDIZED_FAMILY_MEMBERSHIP) &&
      authKeys?.includes('auth:one-pass:family-membership') &&
      getDependentInfo?.canAddFamilyMember &&
      !isQualifiedUser,
    isQualifiedUser: isQualifiedUser,
    isUnverifiedUser: isUnverifiedUser,
    isCommercial:
      currentSponsor?.serviceSector?.key === ServiceSectorKey.commercial,
    isGovernment,
    isUserMA,
    isUserMedSupp,
    isUserAuthenticated: currentState?.isHsidSessionValid,
    isFromAikyamPortal: currentState?.isFromAikyamPortal,
    idToken: currentState?.idToken,
    isImpersonatedView: isImpersonatedView,
    isDependent: !!currentMember?.primaryFamilyMemberCode,
  };
};
